import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Groupper, Header, Image, Placeholder, Icon, Label, Field, Message, Input, Modal, Table, useFrontier, Dropdown, Button } from 'react-frontier2';
import { CDN_URL, PLACEHOLDER_IMG } from '../../AdminConfig';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PaymentMethods, SeatmapSeat, SeatStatus, SectionSeatmap, SectionStatus, SetLoading } from '@arema/components/Classes';
import { addCommas, bindClick, formatSeatNumber } from '@arema/components/Util';
import { SearchEventModal } from '../../components';
import { SeatmapSelect } from '@arema/components';
import { useUser } from '../../AdminHooks';
import { useValidator } from '@arema/components/Hooks';
import { SearchOrder } from '../../AdminClasses';
import moment from 'moment';
import classNames from 'classnames';
import UserAccess from '../../UserAccess';
import API from '../../API';
import Validator from '@arema/components/Validator';
import SearchOrderModal from '../../components/SearchOrderModal';
import '../../style/order_creator.scss'

enum Modals{
	SELECT_EVENT = 1,
	CHANGE_STATUS = 2,
	SEARCH_ORDER = 3,
}

enum CreationType{
	NEW_ORDER = 1,
	EXISTING_ORDER = 2,
	PRINTED_TICKET = 3,
	CREATE_FREE = 4,
}

enum CommissionType{
	UNDEFINED = null,
	YES_COMMISSION = 1,
	NO_COMMISSION = 2,
}

interface DateData{
	date_id: number,
	event_id: number,
	event_name: string,
	date: number,
	venue_id: number,
	venue_name: string,
	city: string,
	sections: {
		section_id: number,
		section_name: string,
		numbered: boolean,
		prices_min: number,
		prices_max: number,
		stage_x: number,
		stage_y: number,
		stage_width: number,
		stage_height: number,
		seats_width: number,
		seats_height: number,
		status?: SectionStatus,
		prices: {
			price_id: number,
			price_name: string,
			cost: number,
			commission: number,
			min_tickets: number,
			max_tickets: number,
			multiple: number,
		}[]
	}[]
}

interface CartItem{
	section_id: number,
	section_name: string,
	price_id: number,
	price_name: string,
	seat_id?: number,
	seat_row?: string,
	seat_number?: string,
	seat_section?: string,
	quantity: number,
	ticket_cost: number
	commission: number,
	status: SeatStatus,
}

interface DeliveryMethod{
	delivery_id: number,
	method_name: string,
	delivery_cost: number
}

interface SectionItemProps{
	meta?: any,
	text?: any,
	onClick?: ()=>void,
	placeholder?: boolean,
	toggle?: boolean,
	selected?: boolean,
	disabled?: boolean,
}
var SectionItem = (props: SectionItemProps)=>{
	if(props.placeholder){
		return <div className="section-item placeholder">
			<div className="price">
				<Placeholder width={30} />
			</div>
			<div className="name">
				<Placeholder />
			</div>
			{!!props.toggle && (
				<div className="toggle">
					<Icon name='circle' solid={false} />
				</div>
			)}
		</div>
	}
	return <div className={classNames('section-item', {
		disabled: props.disabled
	})} onClick={props.onClick}>
		<div className="price">{props.meta}</div>
		<div className="name">{props.text}</div>
		{!!props.toggle && (
			<div className="toggle">
				<Icon name={props.selected ? 'circle-check' : 'circle'} solid={!!props.selected} />
			</div>
		)}
	</div>
}

interface CartCardProps{
	item: CartItem,
	commission?: boolean,
}
var CartCard = (props: CartCardProps)=>{
	var { t } = useTranslation();
	return <div className="cart-item">
		<div className="left">
			<div className="section">{props.item.section_name}</div>
			<div className="seat">
				{props.item.seat_id ? <>
					{t('common.seat')}
					{' '}
					{!!(props.item.seat_row || props.item.seat_number) && (
						formatSeatNumber(props.item.seat_row, props.item.seat_number)
					)}
					{!!props.item.seat_section && props.item.seat_section.length>0 && (
						<div className="section"> · {props.item.seat_section}</div>
					)}
				</> : <>
					<Label>x{props.item.quantity}</Label> {props.item.price_name}
				</>}
			</div>
		</div>
		<div className="right">
			<div className="total">${addCommas((props.item.ticket_cost+(props.commission===false ? 0 : props.item.commission))*props.item.quantity)}</div>
		</div>
	</div>
}

var OrderCreator = ()=>{
	var { t } = useTranslation();
	var { hasAccess } = useUser();
	var { Toast } = useFrontier();
	var navigate = useNavigate();
	var params = useParams<{ date: string }>();
	var [loadError, setLoadError] = useState<string>(null);
	var [date, setDate] = useState<DateData>(null);
	var [section, setSection] = useState<DateData["sections"][0]>(null);
	var [selectedPrice, setSelectedPrice] = useState<number>(null);
	var [checkingOut, setCheckingOut] = useState<boolean>(false);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [cart, setCart] = useState<CartItem[]>([]);
	var [seatmap, setSeatmap] = useState<SectionSeatmap>(null);
	var [selectedStatus, setSelectedStatus] = useState<SeatStatus>(null);
	var [statusComment, setStatusComment] = useState<string>('');
	var [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethod[]>(null);
	var CreationForm = useValidator({
		delivery: null,
		type: null,
		expiration: null,
		order_hash: null,
		email: null,
		new_order_hash: null,
		no_commission: CommissionType.UNDEFINED,
		comments: '',
	}, {
		delivery: [{ rule: (v, form)=>{
			if(form.type===CreationType.NEW_ORDER){
				return Validator.validate(['empty'], v, form);
			}
			return true;
		}, label: t('common.delivery') }],
		type: [{ rule: 'empty', label: t('order_creator.creation_type.label') }],
		expiration: [{ rule: (v, form)=>{
			if([CreationType.PRINTED_TICKET, CreationType.CREATE_FREE].indexOf(form.type)!==-1) return true;
			else return Validator.validate(['number'], v, form);
		}, label: t('common.expiration') }],
		email: [{ rule: 'email', skipEmpty: true, label: t('common.email') }],
		order_hash: [{ rule: (v, form)=>{
			if(form.type===CreationType.EXISTING_ORDER){
				return Validator.validate(['empty'], v, form);
			}
			return true;
		}, prompt: t('order_creator.form_prompts.empty_order')}],
		comments: [{ rule: (v, form)=>{
			if(form.type!==CreationType.CREATE_FREE) return true;
			return Validator.validate([{ rule: 'minLength', params: [3] }], v, form);
		}, label: t('order_creator.free_comments') }],
		no_commission: [{ rule: (v, form)=>{
			if(form.type!==CreationType.PRINTED_TICKET) return true;
			return v!==CommissionType.UNDEFINED;
		} }]
	})

	useEffect(()=>{
		if(!section){
			setCart([]);
			setSelectedPrice(null);
			setCheckingOut(null);
			return;
		}
		if(!selectedPrice){
			if(section.prices.length==1){
				setSelectedPrice(section.prices[0].price_id);
			}
		}
	}, [section, selectedPrice]);

	useEffect(()=>{
		if(section && section.numbered && selectedPrice && (!seatmap || seatmap.section_id!==section.section_id)){
			setSeatmap(null);
			API.getDateSeatmap(date.date_id, section.section_id).then(res=>{
				if(res.error){
					setSection(null);
					return Toast.error(res.message);
				}
				setSeatmap(res.data);
			}).catch(err=>{
				return Toast.error(t('common.unexpected_code', { code: 'LCL-ORDCRT-SMP' }))
			})
		}
	}, [section, seatmap, selectedPrice]);

	useEffect(()=>{
		if(!checkingOut || !!deliveryMethods || !date) return;
		API.getDateDelivery(date.date_id).then(res=>{
			if(res.error){
				setCheckingOut(false);
				return Toast.error(res.message);
			}
			setDeliveryMethods(res.data);
		}).catch(err=>{
			setCheckingOut(false);
			Toast.error(t('common.unexpected_code', 'LCL-ORDCRT-DLMG'));
		})
	}, [checkingOut, deliveryMethods, date]);

	useEffect(()=>{
		if(!params.date || Number.isNaN(parseInt(params.date))) return;
		var date_id = parseInt(params.date);
		if(date && date.date_id===date_id) return;

		setDate(null);
		setSection(null);
		setCart([]);
		setSeatmap(null);
		setSelectedPrice(null);
		setDeliveryMethods(null);
		CreationForm.setValue('new_order_hash', null);
		API.getOrderCreatorDate(parseInt(params.date)).then(res=>{
			if(res.error) return setLoadError(res.message);
			setDate(res.data);
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { code: 'LCL-ORDCRT-DTGT' }));
		});
	}, [params.date]);

	var changeDate = (ev: any, date: { date_id: number })=>{
		navigate(`/orders/create/${date.date_id}`, {
			replace: true
		})
		return true;
	}

	var goBack = ()=>{
		if(CreationForm.data.new_order_hash){
			CreationForm.reset();
			setCheckingOut(false);
			setSelectedPrice(null);
			setSection(null);
			return;
		}
		if(checkingOut){
			CreationForm.reset();
			return setCheckingOut(false);
		}
		if(section){
			if(section.numbered){
				if(selectedPrice && section.prices.length>1) return setSelectedPrice(null);
			}
			setSelectedPrice(null);
			setSection(null);
		}
	}

	var addTicket = (section_id: number, price_id: number, remove: boolean=false)=>{
		return ()=>{
			var ct = cart ? [...cart] : [];
			var cx = ct.findIndex(a=>a.price_id==price_id && a.section_id==section_id);
			var section = date.sections.find(a=>a.section_id==section_id);
			if(!section) return;
			var price = section.prices.find(a=>a.price_id==price_id);
			if(!price) return;
			
			var max_tickets = price.max_tickets%price.multiple!==0 ? price.max_tickets-(price.max_tickets%price.multiple) : price.max_tickets;
			if(cx==-1 && !remove){
				ct.push({
					section_id: section.section_id,
					section_name: section.section_name,
					price_id: price.price_id,
					price_name: price.price_name,
					quantity: 0,
					ticket_cost: price.cost,
					commission: price.commission,
					status: SeatStatus.FREE,
				});
				cx = ct.length-1;
			}
			
			ct[cx].quantity = Math.min(max_tickets, Math.max(0, ct[cx].quantity+((remove ? -1 : 1)*price.multiple)));
			setCart(ct);
		}
	}

	var editCart = (section_id: number, price_id: number)=>{
		return (data: React.ChangeEvent<HTMLInputElement>)=>{
			var ct = cart ? [...cart] : [];
			var cx = ct.findIndex(a=>a.price_id==price_id && a.section_id==section_id);
			var section = date.sections.find(a=>a.section_id==section_id);
			if(!section) return;
			var price = section.prices.find(a=>a.price_id==price_id);
			if(!price) return;

			var raw_amount = data.target.value.replace(/\D/g, "");
			var amount = parseInt(raw_amount.length==0 ? '-1' : raw_amount);

			if(cx==-1){
				ct.push({
					section_id: section.section_id,
					section_name: section.section_name,
					price_id: price.price_id,
					price_name: price.price_name,
					quantity: 0,
					ticket_cost: price.cost,
					commission: price.commission,
					status: SeatStatus.FREE,
				});
				cx = ct.length-1;
			}
			ct[cx].quantity = Math.max(0, amount);
			setCart(ct);
		}
	}

	var onSeatsSelected = (seats: SeatmapSeat[])=>{
		if(!section || !selectedPrice) return;
		var price = section.prices.find(a=>a.price_id==selectedPrice);
		if(!price) return;
		// if(seats.length>price.max_tickets) return; 

		var new_cart = seats.map(a=>({
			section_id: section.section_id,
			section_name: section.section_name,
			price_id: price.price_id,
			price_name: price.price_name,
			seat_id: a.seat_id,
			seat_row: a.seat_row,
			seat_number: a.seat_number,
			seat_section: a.seat_section,
			quantity: 1,
			ticket_cost: (a.seat_cost || price.cost),
			commission: (a.seat_commission || price.commission),
			status: a.ticket_id ? SeatStatus.BLOCKED : a.seat_status,
		}))
		setCart(new_cart);
		return true;
	}

	var onOrderSelected = (order: SearchOrder)=>{
		if(!order) return;
		CreationForm.setValue('order_hash', order.order_hash);
		setShownModal(null);
	}

	var completeOrder = (setLoading: SetLoading)=>{
		var { valid } = CreationForm.validate();
		if(!valid) return;
		var real_cart = cart.filter(a=>a.quantity>0);
		if(real_cart.length==0) return CreationForm.setPrompts([t('order_creator.empty_cart')])

		var { data } = CreationForm;
		setLoading(true);
		API.createOrder({
			cart: cart.map(a=>({
				price_id: a.price_id,
				seat_id: a.seat_id || null,
				quantity: a.seat_id ? 1 : Math.max(a.quantity, 0)
			})),
			payment_method: data.type===CreationType.CREATE_FREE ? PaymentMethods.FREE : (data.type===CreationType.PRINTED_TICKET ? PaymentMethods.PRINTED_TICKET : null),
			expiration: data.expiration,
			delivery_id: data.delivery,
			email: data.type===CreationType.NEW_ORDER ? data.email : null,
			order_hash: data.type===CreationType.EXISTING_ORDER ? data.order_hash : null,
			comments: data.type===CreationType.CREATE_FREE ? data.comments : null,
			no_commission: data.type===CreationType.PRINTED_TICKET && data.no_commission===CommissionType.NO_COMMISSION,
		}).then(res=>{
			if(res.error) return CreationForm.setPrompts([res.message]);
			setSeatmap(null);
			CreationForm.setValue('new_order_hash', res.data.order_hash);
		}).catch(err=>{
			CreationForm.setPrompts([t('common.unexpected_code', { code: 'LCL-ORDC-CRTOR' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendSeatStatus = (status: SeatStatus, comment?: string)=>{
		if(!hasAccess(UserAccess.DATES.CHANGE_DATE_SEAT_STATUS)) return Toast.error(t('common.no_access_action'));
		var seats = cart.filter(a=>!!a.seat_id).map(a=>a.seat_id);
		if(seats.length==0){
			return Toast.error(t('order_creator.empty_cart'));
		}
		if(status!==SeatStatus.FREE && (!comment || comment.length<3)){
			return Toast.error(t('order_creator.invalid_status_comment'))
		}

		setShownModal(null);
		var tx = Toast.show(t('order_creator.seat_status_changing'), {
			loading: true,
			dismissable: false
		});
		API.setDateSeatStatus(date.date_id, status, seats, comment).then(res=>{
			if(res.error) return Toast.error(res.message);
			var stm = {...seatmap};
			var ct = [...cart];
			for(var i of seats){
				var ix = stm.seats.findIndex(a=>a.seat_id===i);
				if(ix!==-1){
					stm.seats[ix].seat_status = status;
				}
				var cix = ct.findIndex(a=>a.seat_id===i);
				if(cix!==-1){
					ct[cix].status = status;
				}
			}
			setSeatmap(stm);
			setCart(ct);
			Toast.success(t('order_creator.seat_status_updated', { status: t(`seat_status.status_${status}`) }));
		}).catch(err=>{
			return Toast.error(t('common.unexpected_code', { code: 'LCL-ORCRT-CRTSTT-1' }));
		}).finally(()=>{
			Toast.dismiss(tx);
		})
	}

	var setCartStatus = (status: SeatStatus)=>{
		if(cart.length===0) return null;
		if(status===SeatStatus.FREE){
			return ()=>{
				sendSeatStatus(status);
			}
		}else{
			return ()=>{
				setSelectedStatus(status);
				setStatusComment('');
				setShownModal(Modals.CHANGE_STATUS);
			}
		}
	}

	var { tickets_total, commission_total, cart_total, invalid_cart, cart_count } = useMemo(()=>{
		var cart_total : number = 0, tickets_total : number = 0, commission_total : number = 0, cart_count: number = 0;
		var invalid_cart : boolean = false;
		var is_free = CreationForm.data.type===CreationType.CREATE_FREE;

		if(!!cart){
			for(var i of cart){
				if(i.quantity<=0) continue;
				if(i.status!==SeatStatus.FREE) invalid_cart = true;
				cart_count += i.quantity;
				if(is_free) continue;
				var ticket_commission = CreationForm.data.no_commission===CommissionType.NO_COMMISSION ? 0 : i.commission;

				cart_total += (i.ticket_cost+ticket_commission)*i.quantity;
				tickets_total += i.ticket_cost*i.quantity;
				commission_total += ticket_commission*i.quantity;
			}
		}
		if(cart_count<=0) invalid_cart = true;
		return { cart_total, tickets_total, commission_total, invalid_cart, cart_count };
	}, [cart, CreationForm.data.type, CreationForm.data.no_commission]);

	var price = useMemo(()=>{
		return !!selectedPrice && section ? section.prices.find(a=>a.price_id===selectedPrice) : null;
	}, [selectedPrice, section]);

	var selected_delivery = useMemo(()=>{
		return !!CreationForm.data.delivery && !!deliveryMethods ? deliveryMethods.find(a=>a.delivery_id===CreationForm.data.delivery) : null;
	}, [deliveryMethods, CreationForm.data.delivery])

	var ACCESS = useMemo(()=>{
		return {
			CHANGE_DATE_SEAT_STATUS: hasAccess(UserAccess.DATES.CHANGE_DATE_SEAT_STATUS),
			CREATE_PRINTED_TICKET: hasAccess(UserAccess.ORDERS.CREATE_PRINTED_TICKET),
			CREATE_FREE: hasAccess(UserAccess.ORDERS.CREATE_FREE)
		}
	}, []);

	var ReturnComponents = <>
		<div className="sections">
			<div className="section-item" onClick={bindClick(setSection, null)}>
				<div className="price small">
					<Icon name="reply" />
				</div>
				<div className="name">{t('common.return')}</div>
			</div>
			<div className="section-item placeholder">
				<div className="name" style={{ fontWeight: 'bold', textAlign: 'center' }}>{section?.section_name}</div>
			</div>
		</div>
	</>

	return <div className={classNames('ar order-creator-screen', { full: !!section?.numbered && !checkingOut })}>
		<div className={'event'} data-placeholder={!date || undefined}>
			{!date ? (
				<div className={'poster'} />
			) : (
				<Image className={'poster'} src={`${CDN_URL}/events/${date.event_id}/400.webp`} fallback={PLACEHOLDER_IMG} />
			)}
			<div className={'info'}>
				{!date ? <>
					<Placeholder width={200} height={20} />
					<Placeholder width={50} height={14} />
					<Placeholder width={50} height={14}/>
				</> : <>
					<div className={'name'}>{date.event_name}</div>
					{/* <div className={'date'}>{moment.unix(date.date).format('DD/MMM/YY HH:mm')}</div> */}
					<div className={'meta'}>{date.venue_name}</div>
					<div className={'meta'}>{date.city}</div>
				</>}
				<Button size='tiny' text='Cambiar' iconName='reply' onClick={bindClick(setShownModal, Modals.SELECT_EVENT)} />
			</div>
		</div>
		<Groupper 
			className={classNames({
				small: !section || !section.numbered || loadError || !selectedPrice || checkingOut
			})}
			titleRight={((!!section?.numbered && !!selectedPrice) || checkingOut) && (
				<Button text={t('common.return')} onClick={goBack} iconName='reply' size='tiny' />
			)}
			fitted={section && section?.numbered && !!selectedPrice && !checkingOut}
			title={(
				!date ? (
					<Placeholder width={200} />
				) : moment.unix(date.date).format('DD/MMM/YY HH:mm')
			)}
			actions={CreationForm.data.new_order_hash ? null : !checkingOut ? (
				section ? (
					<Button text={t('common.continue')} color='black' onClick={bindClick(setCheckingOut, true)} disabled={invalid_cart || cart.length==0} />
				) : null
			) : (
				<Button text={t('common.complete')} color='black' onClick={completeOrder} disabled={invalid_cart} />
			)}
		>
			{loadError ? (
				<Header text={t('common.error')} subtext={loadError} />
			) : !date ? (
				<div className="sections">
					<SectionItem meta={null} text={null} placeholder />
					<SectionItem meta={null} text={null} placeholder />
					<SectionItem meta={null} text={null} placeholder />
				</div>
			) : !section ? (
				date.sections.length==0 ? (
					<Header text={t('order_creator.no_sections')} />
				) : (
					<div className="sections">
						{date.sections.map(a=>(
							<SectionItem 
								key={`CRTSC-${a.section_id}`}
								onClick={bindClick(setSection, a)} 
								disabled={(a.status===SectionStatus.SOLD_OUT || a.prices_min===null)}
								meta={(
									(a.status===SectionStatus.SOLD_OUT || a.prices_min===null) ? (
										t('order_creator.sold_out')
									) : <>
										${addCommas(a.prices_min, true, true)}
										{a.prices_min!==a.prices_max ? (
											`- $${addCommas(a.prices_max, true, true)}`
										) : null}
									</>
								)}
								text={a.section_name}
							/>
						))}
					</div>
				)
			) : CreationForm.data.new_order_hash ? <>
				<Header
					iconName="check-circle"
					style={{ fontSize: 20, marginTop: 15 }} 
					text={t('order_creator.order_created')} 
					subtext={t('order_creator.order_created_message')} 
					subtextStyle={{ fontSize: 14 }} 
				/>
				<div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
					<Label style={{ marginBottom: 10, minWidth: 200 }}>
						{CreationForm.data.new_order_hash}
					</Label>
					<Button text={t('order_creator.view_order')} color="black" style={{ fontSize: 14, width: 200 }} as={Link} to={`/orders/${CreationForm.data.new_order_hash}`} />
				</div>
			</> : checkingOut ? <>
				<div className="checkout">
					<div className="title">{t('common.tickets', { count: cart_count })}</div>
					{cart.map(a=>(
						<CartCard item={a} commission={CreationForm.data.no_commission!==CommissionType.NO_COMMISSION} key={`CRTCRD-${a.seat_id}-${a.price_id}`} />
					))}

					<div style={{ height: 10 }} />
					<Dropdown
						label={t('order_creator.creation_type.label')}
						value={CreationForm.data.type}
						onValueChange={CreationForm.onDataChange('type')}
						style={{ marginBottom: 10 }}
						items={[
							{ text: t('order_creator.creation_type.new_order'), value: CreationType.NEW_ORDER },
							// { text: t('order_creator.creation_type.add_existing'), value: CreationType.EXISTING_ORDER },
							...(ACCESS.CREATE_PRINTED_TICKET ? [
								{ text: t('order_creator.creation_type.printed_ticket'), value: CreationType.PRINTED_TICKET }
							] : []),
							...(ACCESS.CREATE_FREE ? [
								{ text: t('order_creator.creation_type.create_free'), value: CreationType.CREATE_FREE }
							] : []),
						]}
					/>
					{CreationForm.data.type===CreationType.EXISTING_ORDER ? (
						<Field label={t('order_creator.select_order')}>
							<Input placeholder={t('order_creator.select_order')} error={CreationForm.errors.order_hash} readonly value={CreationForm.data.order_hash} buttons={(
								<Button text={t('common.select')} onClick={bindClick(setShownModal, Modals.SEARCH_ORDER)} />
							)} />
						</Field>
					) : CreationForm.data.type===CreationType.NEW_ORDER ? (
						<Input label={t('common.email')} value={CreationForm.data.email} error={CreationForm.errors.email} onChange={CreationForm.onDataChange('email')} comment={t('common.optional')} />
					) : CreationForm.data.type===CreationType.CREATE_FREE ? (
						<Input textarea label={t('order_creator.free_comments')} comment={t('order_creator.free_comments_text')} value={CreationForm.data.comments} onChange={CreationForm.onDataChange('comments')} error={CreationForm.errors.comments} />
					) : null}
					{[CreationType.PRINTED_TICKET, CreationType.CREATE_FREE].indexOf(CreationForm.data.type)==-1 && <>
							<Dropdown
								label={t('order_creator.expiration_time')}
								value={CreationForm.data.expiration}
								onValueChange={CreationForm.onDataChange('expiration')}
								style={{ marginBottom: 10 }}
								items={[5, 10, 30, 60, 120, 1440, 2880].map(a=>({
									value: a*60,
									text: a>=60 ? `${t('common.time.hour', { count: Math.floor(a/60) })}` : t('common.time.minutes', { count: a })
								}))}
							/>
						{!!CreationForm.data.type && (
							<Message type='info' style={{ marginBottom: 10 }}>
								{t('order_creator.creating_not_paid')}
							</Message>
						)}
					</>}

					{CreationForm.data.type===CreationType.PRINTED_TICKET && <>
						<Dropdown
							label={t('order_creator.no_commission')}
							value={CreationForm.data.no_commission}
							onValueChange={CreationForm.onDataChange('no_commission')}
							items={[
								{ text: t('order_creator.no_commission_no'), value: CommissionType.NO_COMMISSION },
								{ text: t('order_creator.no_commission_yes'), value: CommissionType.YES_COMMISSION }
							]}
						/>
						{/* <Checkbox label={t('order_creator.no_commission')} checked={CreationForm.data.no_commission} onChange={CreationForm.onDataChange('no_commission')} /> */}
					</>}
						
					{CreationForm.data.type===CreationType.NEW_ORDER && <>
						<div className="title" style={{ marginTop: 15 }}>{t('order_creator.delivery')}</div>
						{!deliveryMethods ? (
							<SectionItem toggle placeholder />
						) : deliveryMethods.map(a=>(
							<SectionItem toggle text={a.method_name} meta={`$${addCommas(a.delivery_cost, true, true)}`} selected={CreationForm.data.delivery===a.delivery_id} onClick={bindClick(CreationForm.onDataChange('delivery'), a.delivery_id)} />
						))}
					</>}

					<Message type='error' list={CreationForm.prompts} />
				</div>
			</> : (
				section.numbered ? (
					!!selectedPrice ? <>
						<Groupper.Divider className='divider' top>
							<div className="left">
								<Label className='price'>
									<div className="price">${addCommas(price.cost+price.commission, true, true)}</div>
									<div className="name">{price.price_name}</div>
								</Label>
								<Label>
									<div className="name">{cart.length} {t('common.tickets', { count: cart.length })}</div>
									<div className="price right">
										${addCommas(cart_total, true, true)}
									</div>
								</Label>
								{!!invalid_cart && (
									<Label color='red'>
										<div className="name">{t('order_creator.invalid_cart')}</div>
									</Label>
								)}
							</div>
							<div className="right">
								<Button size='small' text={t('common.tools')} iconRight='caret-down' iconRightStyle={{ marginRight: 0 }} menu={[
									{ text: t('order_creator.tools.empty_cart'), onClick: bindClick(setCart, []) },
									{ text: t('order_creator.tools.change_status'), items: (
										[SeatStatus.FREE, SeatStatus.BLOCKED, SeatStatus.FREE_PDV, SeatStatus.HIDDEN, SeatStatus.LOCKED].map(a=>(
											{ text: t(`seat_status.status_${a}`), onClick: setCartStatus(a) }
										))
									)}
								]} />
								{/* <Dropdown
									icon={null}
									trigger={(
										
									)}
									value={null}
									direction='left'
								>
									<Dropdown.Menu>
										<Dropdown.Item text={t('order_creator.tools.empty_cart')} onClick={bindClick(setCart, [])} />
										<Dropdown.Item style={{ padding: 0 }} disabled={cart.length==0}>
											<Dropdown text={t('order_creator.tools.change_status')} style={{ padding: 12 }}>
												<Dropdown.Menu>
													{[SeatStatus.FREE, SeatStatus.BLOCKED, SeatStatus.FREE_PDV, SeatStatus.HIDDEN, SeatStatus.LOCKED].map(a=>(
														<Dropdown.Item text={t(`seat_status.status_${a}`)} onClick={setCartStatus(a)} />
													))}
												</Dropdown.Menu>
											</Dropdown>
										</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown> */}
							</div>
						</Groupper.Divider>
						<div className='container'>
							<SeatmapSelect
								loading={!seatmap}
								defaultStyle={false}
								style={{ height: '100%' }}
								seatmap={seatmap}
								selected={cart.filter(a=>!!a.seat_id).map(a=>a.seat_id)}
								onSelected={onSeatsSelected}
								selectBlocked={ACCESS.CHANGE_DATE_SEAT_STATUS}
							/>
						</div>
					</> : <>
						{ReturnComponents}
						<div className="ar prices selectable">
							{section.prices.map(a=>(
								<div className="price" onClick={bindClick(setSelectedPrice, a.price_id)}>
									<div className="cost">${addCommas(a.cost+a.commission, true, true)}</div>
									<div className="name">{a.price_name}</div>
								</div>
							))}
						</div>
					</>
				) : <>
					{ReturnComponents}
					<div className="ar prices">
						{section.prices.map(a=>{
							var quantity = 0;
							if(cart){
								var c = cart.find(c=>c.price_id==a.price_id && c.section_id==section.section_id);
								if(c) quantity = c.quantity;
							}

							return <div className="price" key={`sect${section.section_id}-pr${a.price_id}`}>
								<div className="cost">${addCommas(a.cost+a.commission, true, true)}</div>
								<div className="name">{a.price_name}</div>
								<div className="quantity">
									<Button className='minus' color='black' iconName="minus" onClick={addTicket(section.section_id, a.price_id, true)} />
									<input className='amount' value={quantity<=-1 ? '' : quantity} onChange={editCart(section.section_id, a.price_id)} />
									{/* <div className="amount">{quantity}</div> */}
									<Button className='plus' color='black' iconName="plus" onClick={addTicket(section.section_id, a.price_id)} />
								</div>
							</div>
						})}
					</div>
				</>
			)}
			{(checkingOut || (section && !section.numbered)) && !CreationForm.data.new_order_hash && (
				<table className='totals'>
					<tbody>
						<tr>
							<td>{t('common.tickets')}</td>
							<td>${addCommas(tickets_total)}</td>
						</tr>
						<tr>
							<td>{t('common.commission')}</td>
							<td>${addCommas(commission_total)}</td>
						</tr>
						{!!checkingOut && (
							<tr>
								<td>{t('common.delivery')}</td>
								<td>${addCommas(selected_delivery?.delivery_cost || 0)}</td>
							</tr>
						)}
						<tr>
							<td>{t('common.subtotal')}</td>
							<td className='total'>${addCommas(cart_total)}</td>
						</tr>
					</	tbody>
				</table>
			)}
		</Groupper>
		<SearchEventModal date
			open={shownModal===Modals.SELECT_EVENT}
			onClose={bindClick(setShownModal)}
			onDateSelected={changeDate}
		/>
		<SearchOrderModal
			open={shownModal===Modals.SEARCH_ORDER}
			onClose={bindClick(setShownModal)}
			onSelected={onOrderSelected}
		/>
		<Modal open={shownModal===Modals.CHANGE_STATUS} size={'tiny'} onClose={bindClick(setShownModal)}>
			<Modal.Header>{t('order_creator.tools.change_status')}</Modal.Header>
			<Modal.Content>
				<Message centered type='info'>
					{t('order_creator.change_status.subtitle')} <br />
					<div style={{ fontWeight: 'bold', fontSize: 20 }}>{t(`seat_status.status_${selectedStatus}`)}</div>
				</Message>
				<Table style={{ marginTop: 15, marginBottom: 15 }}>
					<Table.Head
						collapsingIndexes={[0, 2, 3]}
						centeredIndexes={[2, 3]}
						data={[t('order_creator.change_status.table_headers', { returnObjects: true })]}
					/>
					<Table.Body data={cart.filter(a=>!!a.seat_id).map(a=>([
						a.seat_id,
						a.section_name,
						<span className='fr mono'>{a.seat_row}</span>,
						<span className='fr mono'>{a.seat_number}</span>,
						a.seat_section
					]))} />
				</Table>
				<Input textarea label={t('order_creator.change_status.input')} comment={<Trans i18nKey={'order_creator.change_status.input_comment'} /> as any} onChange={setStatusComment} value={statusComment} />
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal)} />
				<Button text={t('common.send')} color={'black'} onClick={sl=>{
					sendSeatStatus(selectedStatus, statusComment)
				}} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default OrderCreator;